import React, { useEffect, useRef } from "react";
import { FaEnvelope, FaTimes, FaTrash, FaUser } from "react-icons/fa";

import {
  DEFAULT_AVATAR,
  formatUserDate,
  getRoleInfo,
  getUserAdherents,
  getUserCreatedDate,
  getUserName,
  getUserPhoto,
} from "../utils/adminUsersUtils";

function InfoBox({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
        {label}
      </p>
      <p className="mt-2 break-words text-sm font-bold text-slate-800 dark:text-white/80">
        {value || "Non renseigné"}
      </p>
    </div>
  );
}

function UserDetailsDrawer({
  isOpen,
  user,
  deleting,
  onClose,
  onDelete,
}) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !user) return null;

  const role = getRoleInfo(user.role_id);
  const adherents = getUserAdherents(user);

  return (
    <div className="fixed inset-0 z-[90] flex justify-end bg-slate-950/70 backdrop-blur-sm">
      <aside
        className="h-full w-full max-w-2xl bg-white shadow-2xl dark:bg-slate-900"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex h-full flex-col">
          <div className="bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 p-5 text-white">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <img
                  src={getUserPhoto(user)}
                  alt={getUserName(user)}
                  className="h-16 w-16 rounded-[1.5rem] border border-white/20 object-cover"
                  onError={(event) => {
                    event.currentTarget.src = DEFAULT_AVATAR;
                  }}
                />

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/60">
                    Fiche utilisateur
                  </p>

                  <h2 className="mt-2 font-titre text-2xl font-bold">
                    {getUserName(user)}
                  </h2>

                  <span
                    className={`mt-3 inline-flex min-h-8 items-center gap-2 rounded-2xl px-3 text-xs font-bold ${role.badge}`}
                  >
                    <span className={`h-2 w-2 rounded-full ${role.dot}`} />
                    {role.label}
                  </span>
                </div>
              </div>

              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25"
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoBox label="ID utilisateur" value={`#${user.id}`} />
              <InfoBox
                label="Création"
                value={formatUserDate(getUserCreatedDate(user))}
              />
              <InfoBox label="Nom" value={user.nom} />
              <InfoBox label="Prénom" value={user.prenom} />
              <InfoBox label="Email" value={user.email} />
              <InfoBox label="Rôle" value={role.label} />
            </div>

            <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                  <FaUser />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                    Judokas rattachés
                  </p>
                  <h3 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
                    {adherents.length} profil{adherents.length > 1 ? "s" : ""}
                  </h3>
                </div>
              </div>

              {adherents.length > 0 ? (
                <div className="mt-4 space-y-3">
                  {adherents.map((adherent) => (
                    <div
                      key={adherent.id}
                      className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950"
                    >
                      <p className="font-bold text-slate-950 dark:text-white">
                        {[adherent.prenom, adherent.nom]
                          .filter(Boolean)
                          .join(" ")}
                      </p>

                      <p className="mt-1 text-xs font-semibold text-slate-400">
                        ID #{adherent.id} · {adherent.club || "Club non renseigné"}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm font-bold text-slate-400 dark:border-white/10 dark:bg-slate-950">
                  Aucun judoka rattaché à ce compte.
                </div>
              )}
            </section>
          </div>

          <div className="sticky bottom-0 grid gap-2 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:grid-cols-2">
            <a
              href={user.email ? `mailto:${user.email}` : undefined}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-sky-200 hover:text-sky-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
            >
              <FaEnvelope />
              Contacter
            </a>

            <button
              type="button"
              onClick={() => onDelete(user)}
              disabled={deleting}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
            >
              <FaTrash />
              {deleting ? "Suppression..." : "Supprimer"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default UserDetailsDrawer;